import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip
} from '@mui/material';
import { ViewColumn as ViewColumnIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

/**
 * Reusable ColumnVisibilityFilter component
 * Allows users to show/hide table columns with a dropdown interface
 */
const ColumnVisibilityFilter = ({ 
  columns = [], 
  visibleColumns = [], 
  onVisibilityChange,
  disabled = false,
  standardColumns = ['eventtime', 'eventsource']
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = (column) => {
    const newVisibleColumns = visibleColumns.includes(column)
      ? visibleColumns.filter(col => col !== column)
      : [...visibleColumns, column];
    
    onVisibilityChange(newVisibleColumns);
  };

  const handleShowAll = () => {
    onVisibilityChange([...columns]);
  };

  const handleHideAll = () => {
    // Always keep at least one standard column visible
    onVisibilityChange([standardColumns[0]]);
  };

  const isStandardColumn = (column) => standardColumns.includes(column);
  const hiddenCount = columns.length - visibleColumns.length;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <IconButton
        onClick={handleClick}
        disabled={disabled}
        size="small"
        sx={{
          color: hiddenCount > 0 ? '#1976d2' : 'inherit',
          backgroundColor: hiddenCount > 0 ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
          '&:hover': {
            backgroundColor: hiddenCount > 0 ? 'rgba(25, 118, 210, 0.2)' : 'rgba(0, 0, 0, 0.04)'
          }
        }}
        title="Column visibility settings"
      >
        <ViewColumnIcon fontSize="small" />
      </IconButton>
      
      {hiddenCount > 0 && (
        <Chip 
          label={`${hiddenCount} hidden`} 
          size="small" 
          color="primary" 
          variant="outlined"
          icon={<VisibilityOffIcon />}
          sx={{ fontSize: 11, height: 24 }}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            minWidth: 250,
            '& .MuiMenuItem-root': {
              padding: '4px 8px'
            }
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Column Visibility
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleShowAll}
              sx={{ fontSize: 11, py: 0.5 }}
            >
              Show All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleHideAll}
              sx={{ fontSize: 11, py: 0.5 }}
            >
              Hide All
            </Button>
          </Box>
        </Box>
        
        <Divider />
        
        {/* Standard Columns */}
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', px: 1 }}>
            Standard Fields
          </Typography>
          {columns.filter(isStandardColumn).map((column) => (
            <MenuItem key={column} sx={{ py: 0.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns.includes(column)}
                    onChange={() => handleColumnToggle(column)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {column.replace(/([A-Z])/g, ' $1').trim()}
                  </Typography>
                }
                sx={{ margin: 0, width: '100%' }}
              />
            </MenuItem>
          ))}
        </Box>

        {/* Custom/Tag Columns */}
        {columns.filter(col => !isStandardColumn(col)).length > 0 && (
          <>
            <Divider />
            <Box sx={{ p: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 'bold', px: 1 }}>
                Custom Fields
              </Typography>
              {columns.filter(col => !isStandardColumn(col)).map((column) => (
                <MenuItem key={column} sx={{ py: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={visibleColumns.includes(column)}
                        onChange={() => handleColumnToggle(column)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {column}
                      </Typography>
                    }
                    sx={{ margin: 0, width: '100%' }}
                  />
                </MenuItem>
              ))}
            </Box>
          </>
        )}
        
        <Divider />
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {visibleColumns.length} of {columns.length} columns visible
          </Typography>
        </Box>
      </Menu>
    </Box>
  );
};

export default ColumnVisibilityFilter;
